import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useRerunnerConfigs } from "hooks/useRerunnerConfigs";
import { useToast } from "components/Toast";
import { ArrowLeft, Save } from "lucide-react";

export default function RerunnerEdit() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = useEffectiveCompanyId();
  const { toast } = useToast();
  const { configs, loading } = useRerunnerConfigs();
  const [saving, setSaving] = useState(false);

  const isAdmin = isInternal(user);

  const config = configs.find((c) => c.clientId === clientId);

  const [form, setForm] = useState({
    clientName: "",
    enabled: false,
    retriableDeclineCodes: "",
    excludedDeclineCodes: "",
    minAmount: "",
    maxAmount: "",
    requestsPerSecond: 2,
    maxConcurrent: 1,
    timeWindowHours: 24,
    schedule: "",
    baseUrl: "",
  });

  useEffect(() => {
    if (!config) return;
    setForm({
      clientName: config.clientName || "",
      enabled: config.enabled ?? false,
      retriableDeclineCodes: (config.retriableDeclineCodes || []).join(", "),
      excludedDeclineCodes: (config.excludedDeclineCodes || []).join(", "),
      minAmount: config.filters?.minAmount ?? "",
      maxAmount: config.filters?.maxAmount ?? "",
      requestsPerSecond: config.rateLimit?.requestsPerSecond ?? 2,
      maxConcurrent: config.rateLimit?.maxConcurrent ?? 1,
      timeWindowHours: config.timeWindowHours ?? 24,
      schedule: config.schedule || "",
      baseUrl: config.baseUrl || "",
    });
  }, [config]);

  function onChange(field) {
    return (e) => {
      const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);

    const filters = {};
    if (form.minAmount !== "") filters.minAmount = Number(form.minAmount);
    if (form.maxAmount !== "") filters.maxAmount = Number(form.maxAmount);

    let payload;

    if (isAdmin) {
      const retriable = form.retriableDeclineCodes
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const excluded = form.excludedDeclineCodes
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      payload = {
        clientName: form.clientName,
        enabled: form.enabled,
        retriableDeclineCodes: retriable,
        timeWindowHours: Number(form.timeWindowHours),
        rateLimit: {
          requestsPerSecond: Number(form.requestsPerSecond),
          maxConcurrent: Number(form.maxConcurrent),
        },
      };
      if (excluded.length) payload.excludedDeclineCodes = excluded;
      if (Object.keys(filters).length) payload.filters = filters;
      if (form.schedule) payload.schedule = form.schedule;
      if (form.baseUrl) payload.baseUrl = form.baseUrl;
    } else {
      payload = {
        enabled: form.enabled,
        schedule: form.schedule || undefined,
      };
      if (Object.keys(filters).length) payload.filters = filters;
    }

    try {
      await makeApiRequest(
        "put",
        apiRoutes.updateRerunnerConfig(clientId),
        { id: user.id, accessToken: user.accessToken },
        companyId,
        payload
      );
      toast({ status: "success", title: "Config updated", description: "Changes have been saved." });
      navigate("/rerunner");
    } catch (err) {
      const msg = err?.response?.data?.error || "Something went wrong.";
      toast({ status: "destructive", title: "Update failed", description: msg });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="p-6">Loading...</p>;
  if (!config) return <p className="p-6 text-destructive">Config not found for "{clientId}".</p>;

  return (
    <div>
      <PageHeader
        title="Edit Rerunner Config"
        subtitle={config.clientName}
        actions={
          <div className="flex gap-2">
            <button className="btn btn-ghost" onClick={() => navigate("/rerunner")}>
              <ArrowLeft className="h-4 w-4" />
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        }
      />

      <form onSubmit={handleSave} className="container py-6 space-y-6">
        {/* Enabled Toggle */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Status</h2>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="toggle"
              checked={form.enabled}
              onChange={onChange("enabled")}
            />
            <span className="text-sm">{form.enabled ? "Enabled" : "Disabled"}</span>
          </label>
        </section>

        {/* Admin-only: Identity (read-only) */}
        {isAdmin && (
          <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
            <h2 className="text-lg font-semibold">Identity</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Client ID</label>
                <input type="text" className="input input-bordered w-full bg-muted" value={config.clientId} disabled />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Client Name</label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  value={form.clientName}
                  onChange={onChange("clientName")}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Company</label>
                <input type="text" className="input input-bordered w-full bg-muted" value={config.uniqueCompanyName} disabled />
              </div>
            </div>
          </section>
        )}

        {/* Admin-only: Decline Codes */}
        {isAdmin && (
          <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
            <h2 className="text-lg font-semibold">Decline Codes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Retriable Codes</label>
                <textarea
                  className="input input-bordered w-full min-h-[80px]"
                  placeholder="Comma-separated"
                  value={form.retriableDeclineCodes}
                  onChange={onChange("retriableDeclineCodes")}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Excluded Codes</label>
                <textarea
                  className="input input-bordered w-full min-h-[80px]"
                  placeholder="Comma-separated (optional)"
                  value={form.excludedDeclineCodes}
                  onChange={onChange("excludedDeclineCodes")}
                />
              </div>
            </div>
          </section>
        )}

        {/* Filters (both roles) */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Min Amount ($)</label>
              <input
                type="number"
                className="input input-bordered w-full"
                placeholder="0"
                value={form.minAmount}
                onChange={onChange("minAmount")}
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Max Amount ($)</label>
              <input
                type="number"
                className="input input-bordered w-full"
                placeholder="No limit"
                value={form.maxAmount}
                onChange={onChange("maxAmount")}
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </section>

        {/* Admin-only: Rate Limiting */}
        {isAdmin && (
          <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
            <h2 className="text-lg font-semibold">Rate Limiting</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Requests/sec</label>
                <input
                  type="number"
                  className="input input-bordered w-full"
                  value={form.requestsPerSecond}
                  onChange={onChange("requestsPerSecond")}
                  min="1"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Max Concurrent</label>
                <input
                  type="number"
                  className="input input-bordered w-full"
                  value={form.maxConcurrent}
                  onChange={onChange("maxConcurrent")}
                  min="1"
                />
              </div>
            </div>
          </section>
        )}

        {/* Schedule (both roles) */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Schedule</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Cron Schedule</label>
              <input
                type="text"
                className="input input-bordered w-full"
                placeholder="e.g. 0 */6 * * *"
                value={form.schedule}
                onChange={onChange("schedule")}
              />
            </div>
            {isAdmin && (
              <>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Time Window (hours)</label>
                  <input
                    type="number"
                    className="input input-bordered w-full"
                    value={form.timeWindowHours}
                    onChange={onChange("timeWindowHours")}
                    min="1"
                  />
                </div>
              </>
            )}
          </div>
        </section>

        {/* Admin-only: Base URL */}
        {isAdmin && (
          <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
            <h2 className="text-lg font-semibold">API Configuration</h2>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Base URL</label>
              <input
                type="text"
                className="input input-bordered w-full"
                placeholder="CheckoutChamp API URL"
                value={form.baseUrl}
                onChange={onChange("baseUrl")}
              />
            </div>
          </section>
        )}
      </form>
    </div>
  );
}
